import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Phone, Mail } from "lucide-react";
import { fetchProduct, fetchRelated, fetchSpecs } from "../lib/api";
import { useDocumentMeta } from "../hooks/use-document-meta";
import { useJsonLd } from "../hooks/use-json-ld";
import { productSeo, productJsonLd, productBreadcrumbLd } from "../lib/product-seo";
import { categoryPath } from "../lib/category-slugs";
import { ProductCard } from "../components/ProductCard";
import { ProductImage } from "../components/ProductImage";
import { VideoBand } from "../components/VideoBand";
import { LogisticsBand } from "../components/LogisticsBand";

// Bénéfices par gamme — 3 lignes contextuelles affichées sous le titre produit.
// Fallback générique si la catégorie est inconnue (toujours 3 lignes).
const CATEGORY_BULLETS = {
  frites: [
    "Croustillance constante au service",
    "Conditionnement carton multi-formats",
    "Livraison 24h IDF · France selon zone",
  ],
  viandes: [
    "Sélection boucherie pro, calibrage régulier",
    "Conditionnement IQF prêt à servir",
    "Livraison 24h IDF · France selon zone",
  ],
  volailles: [
    "Pané croustillant, cuisson rapide en service",
    "Format pro IQF ou frais",
    "Livraison 24h IDF · France selon zone",
  ],
  "tortillas-et-pains": [
    "Texture stable, conservation maîtrisée",
    "Calibres pro 25 / 30 cm",
    "Livraison 24h IDF · France selon zone",
  ],
  sauce: [
    "Recette signature, dosage maîtrisé",
    "Bidons, bag-in-box ou portions individuelles",
    "Livraison 24h IDF · France selon zone",
  ],
  conserve: [
    "Conservation longue et stable",
    "Format pro grand volume",
    "Livraison 24h IDF · France selon zone",
  ],
  huile: [
    "Tenue thermique au bain de friture",
    "Bidon pro, rendement maîtrisé",
    "Livraison 24h IDF · France selon zone",
  ],
  "produits-laitiers": [
    "Format pro tranches ou IQF",
    "Conservation maîtrisée",
    "Livraison 24h IDF · France selon zone",
  ],
  dessert: [
    "Portions individuelles prêtes à servir",
    "DLC longue, sans préparation cuisine",
    "Livraison 24h IDF · France selon zone",
  ],
  specialites: [
    "Signature pro, prêt à frire ou réchauffer",
    "Conditionnement carton multi-formats",
    "Livraison 24h IDF · France selon zone",
  ],
  appetizers: [
    "Side rapide, prêt à frire",
    "Conditionnement carton pro",
    "Livraison 24h IDF · France selon zone",
  ],
  emballages: [
    "Format pro, image propre en take-away",
    "Conditionnement carton optimisé",
    "Livraison 24h IDF · France selon zone",
  ],
};

const DEFAULT_BULLETS = [
  "Qualité constante en service",
  "Conditionnement pro (cartons multiples)",
  "Livraison 24h IDF · France selon zone",
];

const productBullets = (product) =>
  CATEGORY_BULLETS[product?.category] || DEFAULT_BULLETS;

// Description B2B contextuelle. On compose à partir des champs réellement
// disponibles dans les données locales (nom, marque, catégorie, hook).
// Pas de spec technique inventée. Sert de fallback quand le produit n'a
// ni description_longue ni description renseignée dans le CMS.
const buildDescription = (product) => {
  if (!product) return "";
  const { name, brand, category_label, hook } = product;
  const categoryHuman = (category_label || "premium").toLowerCase();
  const opener = brand
    ? `${name} est une référence ${brand} de la gamme ${categoryHuman}, distribuée auprès des pros de la restauration rapide.`
    : `${name} fait partie de la gamme ${categoryHuman}, distribuée auprès des pros de la restauration rapide.`;
  const middle = hook ? ` ${hook}` : "";
  const closing =
    " Notre équipe peut vous orienter selon votre activité, vos volumes et vos contraintes de livraison.";
  return `${opener}${middle}${closing}`;
};

// Pick the longest non-empty product description, fall back to the auto-built
// one. Order: description_longue → description → buildDescription.
const productDescription = (product) =>
  (product.description_longue && product.description_longue.trim()) ||
  (product.description && product.description.trim()) ||
  buildDescription(product);

// Order of the fiche technique rows. Optional keys (those whose value is
// null in generateProductSpecs) are skipped at render time.
const SPEC_ROWS = [
  ["Gamme", "gamme"],
  ["Marque", "marque"],
  ["Type de produit", "typeProduit"],
  ["Public cible", "publicCible"],
  ["Référence", "reference"],
  ["Format", "format"],
  ["Conditionnement", "conditionnement"],
  ["Unités par colis", "unitesParColis"],
  ["Colis par palette", "colisParPalette"],
  ["Poids / Volume", "poids"],
  ["Origine", "origine"],
  ["Certifications", "certifications"],
  ["Allergènes", "allergenes"],
  ["Usage recommandé", "usage"],
  ["Préparation / service", "preparation"],
  ["Température de conservation", "temperature"],
  ["Conservation", "conservation"],
  ["Disponibilité", "disponibilite"],
];

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [specs, setSpecs] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const seo = product ? productSeo(product) : null;
  useDocumentMeta({
    title: seo ? seo.title : notFound ? "Produit introuvable" : null,
    description: seo ? seo.description : null,
    canonicalPath: product ? `/produit/${product.slug}` : null,
    robots: notFound ? "noindex" : "index",
  });
  useJsonLd(`product:${slug}`, product ? productJsonLd(product) : null);
  useJsonLd(`breadcrumb:product:${slug}`, product ? productBreadcrumbLd(product) : null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true); setNotFound(false);
    fetchProduct(slug)
      .then((p) => {
        setProduct(p);
        fetchRelated(slug).then(setRelated);
        fetchSpecs(slug).then(setSpecs);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <main className="min-h-screen pt-40 px-6 text-center text-zinc-400" data-testid="product-loading">Chargement...</main>;
  if (notFound || !product) {
    return (
      <main className="min-h-screen pt-40 px-6 text-center" data-testid="product-not-found">
        <h1 className="font-display text-4xl mb-4">Produit introuvable</h1>
        <Link to="/catalogue" className="btn-primary inline-flex">Voir le catalogue</Link>
      </main>
    );
  }

  return (
    <main data-testid="product-page" className="bg-[#050505] min-h-screen pt-28 lg:pt-32">
      <section className="px-6 md:px-10 lg:px-14 py-8">
        <Link to="/catalogue" className="tag-label flex items-center gap-2 hover:text-[#FFC300] transition-colors mb-10" data-testid="back-to-catalog">
          <ArrowLeft size={14} /> Retour au catalogue
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div className="product-frame aspect-square lg:aspect-[4/3] relative overflow-hidden">
              <ProductImage
                path={product.image}
                alt={product.image_alt || product.name}
                // LCP of the product page — tell the browser to fetch it
                // with high priority instead of in the lazy tail.
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-contain p-10 md:p-16"
                data-testid="product-image"
              />
              {product.brand && (
                <span className="absolute top-6 left-6 tag-label bg-black text-white px-3 py-1.5">{product.brand}</span>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 flex flex-col"
          >
            <Link to={categoryPath(product.category)} className="tag-label text-[#FFC300] hover:underline mb-5" data-testid="product-category">
              {product.category_label}
            </Link>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95]" data-testid="product-name">
              {product.name}
            </h1>
            {product.hook && (
              <p className="font-body text-xl md:text-2xl text-[#FFC300]/90 mt-5 leading-snug" data-testid="product-hook">
                {product.hook}
              </p>
            )}
            <p className="font-body text-base text-zinc-400 mt-5 leading-relaxed max-w-md" data-testid="product-description">
              {productDescription(product)}
            </p>

            <div className="mt-8 space-y-3 border-t border-white/10 pt-8">
              {productBullets(product).map((f) => (
                <div key={f} className="flex items-center gap-3 font-body text-sm text-zinc-300">
                  <Check size={16} className="text-[#FFC300] shrink-0" /> {f}
                </div>
              ))}
            </div>

            <LogisticsBand product={product} />

            {specs && (
              <div className="mt-10 border-t border-white/10 pt-8" data-testid="product-specs">
                <p className="tag-label text-[#FFC300] mb-5">Fiche technique</p>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                  {SPEC_ROWS.map(([label, key]) =>
                    specs[key] ? <SpecRow key={key} label={label} value={specs[key]} /> : null
                  )}
                </dl>
                <p className="text-[11px] text-zinc-500 mt-5 leading-relaxed">
                  Les conditionnements, disponibilités et informations techniques détaillées sont confirmés lors de la demande de devis.
                </p>
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to={`/contact?produit=${encodeURIComponent(product.name)}`}
                className="btn-primary flex-1 justify-center"
                data-testid="product-request-cta"
              >
                Demander un échantillon <ArrowUpRight size={16} />
              </Link>
              <Link
                to={`/contact?produit=${encodeURIComponent(product.name)}&request=fiche`}
                className="btn-secondary flex-1 justify-center"
                data-testid="product-pdf-cta"
              >
                Recevoir la fiche par email <Mail size={16} />
              </Link>
            </div>

            {/* Tertiary catalogue path — kept text-only so it doesn't fight
                the 2 primary CTAs but surfaces "Recevoir le catalogue"
                on every product page (≈ 256 surfaces for the same lead-gen). */}
            <Link
              to="/catalogue-feuilletable"
              className="mt-4 inline-flex items-center gap-1.5 tag-label text-[#FFC300] hover:underline"
              data-testid="product-catalog-cta"
            >
              Feuilleter le catalogue complet →
            </Link>

            <div className="mt-12 border-t border-white/10 pt-8 space-y-3 text-sm text-zinc-300">
              <a href="tel:+33148680303" className="flex items-center gap-3 hover:text-[#FFC300] transition-colors">
                <Phone size={16} className="text-[#FFC300]" /> 01 48 68 03 03
              </a>
              <a href="mailto:contact@fritesdorees.fr" className="flex items-center gap-3 hover:text-[#FFC300] transition-colors">
                <Mail size={16} className="text-[#FFC300]" /> contact@fritesdorees.fr
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {related.length > 0 && (
        <>
          <VideoBand
            videoSrc="product-fries-ketchup.mp4"
            posterSrc="product-fries-ketchup-poster.jpg"
            eyebrow="Texture · Constance"
            title={<>La différence <br /> se joue <span className="text-[#FFC300]">au gramme.</span></>}
            tagline="Calibrage millimétré, coupe nette, cuisson maîtrisée. Chaque bouchée a la même intensité, service après service."
            height="h-[55vh]"
            testId="product-video-band"
          />
          <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 border-t border-white/5">
            <div className="mb-10 flex items-end justify-between gap-6">
              <div>
                <div className="tag-label mb-3 flex items-center gap-3">
                  <span className="w-10 h-px bg-[#FFC300]"></span>
                  <span>Dans la même catégorie</span>
                </div>
                <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter">Vous aimerez aussi</h2>
              </div>
              <Link to={categoryPath(product.category)} className="tag-label hover:text-[#FFC300] transition-colors hidden md:flex items-center gap-2">
                <span className="hover-underline">Toute la catégorie</span> <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        </>
      )}
    </main>
  );
}

const SpecRow = ({ label, value }) => (
  <div>
    <dt className="tag-label !text-[10px] text-zinc-500 mb-1">{label}</dt>
    <dd className="font-body text-zinc-100 font-medium">{value}</dd>
  </div>
);

